import React, {useState, useEffect, useMemo} from 'react';
import {Container, Row, Col, InputGroup, Button, Alert, FormSelect} from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCalendar, faFileExcel} from '@fortawesome/free-solid-svg-icons';
import {dateToString, dateWithZeroTime, dateWithMaxTime, numberWithCommas, timeToString} from '../../tools';
import {collection, getDocs, query, where, orderBy, QuerySnapshot, QueryDocumentSnapshot} from 'firebase/firestore';
import {appFirestore} from '../../../index';
import {logisticsFBConverter} from '../logistics/LogisticsFBConverter';
import {RRLogisticType} from '../logistics/RRLogisticType';
import {fuelInvoiceConverter} from '../fuel-invoices/fuelInvoiceConverter';
import {sortFuelInvoicesByDateAndMileage} from '../fuel-invoices/fuelInvoiceSorting';
import {trucksFBConverter} from '../trucks/TrucksFBConverter';
import {RRFuelInvoiceWithExtras} from '../../types/RRFuelInvoiceType';
import {RRTruckType} from '../../types/RRTruckType';
import RRTable from '../../components/rr-table/RRTable';
import PriceCalcModal from './PriceCalcModal';
import {utils, writeFile} from 'xlsx';
import {getReportMonthOptions} from './reportMonthOptions';

type RoadBookRow = {
    id?: string;
    date?: Date | null;
    plannedStartTime: Date;
    plannedEndTime: Date;
    actualStartTime: Date | null;
    actualEndTime: Date | null;
    driverName: string;
    startKm: number | null;
    route: string;
    notes: string;
    endKm: number | null;
    totalKm: number | null;
    cost: number | null;
    costExclVat: number | null;
    pricePerKm: number | null;
    priceSource: 'logistic' | 'invoice' | null;
    truckLabel?: string | null;
    invoicesSlice?: RRFuelInvoiceWithExtras[] | null;
    truckReg?: string | null;
    truckId?: string | null;
    truckKey: string;
    pricePerKmExclVat?: number | null;
};

type PriceCalcResult = {
    pricePerKm: number | null;
    priceSource: 'logistic' | 'invoice' | null;
    invoicesSlice: RRFuelInvoiceWithExtras[] | null;
};

type PriceModalState = {
    show: boolean;
    price: number | null;
    truckLabel?: string | null;
    invoicesSlice?: RRFuelInvoiceWithExtras[] | null;
    note?: string;
};

const ALL_TRUCKS_KEY = 'ALL_TRUCKS';
const NO_TRUCK_KEY = 'NO_TRUCK';
const CUSTOM_PERIOD_KEY = 'CUSTOM_PERIOD';

const formatTimeRange = (startTime: Date | null, endTime: Date | null): string => {
    const start = timeToString(startTime);
    const end = timeToString(endTime);

    if (!start && !end) return '';

    return `${start || '—'}–${end || '—'}`;
};

interface RoadBooksViewProps {
    initialPeriodFrom?: Date;
    initialPeriodTo?: Date;
    embedded?: boolean;
}

const parseNumeric = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return isNaN(value) ? null : value;
    if (typeof value === 'string') {
        const sanitized = value.replace(/[^0-9.,-]+/g, '').replace(/\s+/g, '');
        if (!sanitized) return null;
        const normalized = sanitized.indexOf(',') !== -1 && sanitized.indexOf('.') === -1 ? sanitized.replace(/,/g, '.') : sanitized;
        const parsed = parseFloat(normalized);
        return isNaN(parsed) ? null : parsed;
    }
    return null;
};

const computePricePerKmFromLogistic = (
    logistic: RRLogisticType,
    invoicesMap: Map<string, RRFuelInvoiceWithExtras[]>
): PriceCalcResult => {
    const candidateFields = ['pricePerKm', 'price_per_km', 'priceKm', 'price_km', 'price'];
    let pricePerKm: number | null = null;
    let priceSource: 'logistic' | 'invoice' | null = null;
    let invoicesSlice: RRFuelInvoiceWithExtras[] | null = null;

    for (const field of candidateFields) {
        const parsed = parseNumeric((logistic as Record<string, unknown>)[field]);
        if (parsed !== null && parsed > 0) {
            pricePerKm = parsed;
            priceSource = 'logistic';
            break;
        }
    }

    if (!pricePerKm || pricePerKm === 0) {
        const truckId = logistic.truck?.id ?? '';
        if (truckId) {
            const sourceInvoices = invoicesMap.get(truckId);
            if (sourceInvoices && sourceInvoices.length > 0) {
                const reportDate = logistic.reportStartDate ? new Date(logistic.reportStartDate) : (logistic.startDate ? new Date(logistic.startDate) : undefined);
                const normalizedReportDate = reportDate ? dateWithZeroTime(reportDate) : undefined;
                const closingInvoice = normalizedReportDate
                    ? sourceInvoices.find(inv => {
                        const normalizedInvoiceDate = dateWithZeroTime(inv.invDate);
                        return normalizedInvoiceDate ? normalizedInvoiceDate >= normalizedReportDate : false;
                    }) ?? null
                    : null;

                if (closingInvoice) {
                    const invPrice = Number(closingInvoice.price || 0);
                    const invFuel = Number(closingInvoice.fuelAmount || 0);
                    const invKm = Number(closingInvoice.totalKm || 0);
                    const computedAvg = closingInvoice.avgPriceForKm !== undefined && closingInvoice.avgPriceForKm !== null
                        ? Number(closingInvoice.avgPriceForKm)
                        : (invKm > 0 ? ((invFuel * invPrice) / invKm) : 0);
                    if (computedAvg > 0) {
                        pricePerKm = computedAvg;
                        priceSource = 'invoice';
                        invoicesSlice = [closingInvoice];
                    }
                }
            }
        }
    }

    return {pricePerKm: pricePerKm ?? null, priceSource, invoicesSlice};
};

const buildTruckKey = (truckId?: string | null, truckLabel?: string | null, truckReg?: string | null): string => {
    if (truckId && truckId.trim().length > 0) return `id:${truckId.trim()}`;
    const labelPart = (truckLabel || '').trim();
    const regPart = (truckReg || '').trim();
    if (labelPart || regPart) return `desc:${labelPart}|${regPart}`;
    return NO_TRUCK_KEY;
};

const formatErrorMessage = (err: unknown): string => {
     if (!err) return '';
     if (err instanceof Error) return err.message;
     if (typeof err === 'object' && 'message' in err) {
         const msg = (err as {message?: unknown}).message;
         return typeof msg === 'string' ? msg : JSON.stringify(msg);
     }
     if (typeof err === 'string') return err;
     try {
         return JSON.stringify(err);
     } catch (_err) {
         return String(err);
     }
 };

const formatTruckLabel = (label?: string | null, reg?: string | null): string => {
     const labelPart = (label ?? '').trim();
     const regPart = (reg ?? '').trim();
     if (labelPart && regPart) return `${labelPart} / ${regPart}`;
     if (labelPart) return labelPart;
     if (regPart) return regPart;
     return 'Без автомобил';
 };

const getInvoiceTruckId = (inv: RRFuelInvoiceWithExtras): string | null => {
    const truckRef = (inv as {truck?: {id?: string}}).truck;
    return inv.truckId ?? truckRef?.id ?? null;
};

const RoadBooksView: React.FC<RoadBooksViewProps> = ({initialPeriodFrom, initialPeriodTo, embedded = false}) => {
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getReportMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[embedded ? 0 : 1] ?? monthOptions[0];
    const hasInitialPeriod = initialPeriodFrom !== undefined || initialPeriodTo !== undefined;
    const shouldLoadInitially = hasInitialPeriod || embedded;
    const initialFrom = dateWithZeroTime(initialPeriodFrom ?? defaultMonthOption.periodFrom);
    const initialTo = dateWithMaxTime(initialPeriodTo ?? defaultMonthOption.periodTo);
    const initialMonth = monthOptions.find(option =>
        dateToString(option.periodFrom) === dateToString(initialFrom) &&
        dateToString(option.periodTo) === dateToString(initialTo)
    );

    const [working, setWorking] = useState(false);
    const [showCompletedCourses, setShowCompletedCourses] = useState<boolean>(!embedded);
    const [periodFrom, setPeriodFrom] = useState<Date | undefined>(shouldLoadInitially ? initialFrom : undefined);
    const [periodTo, setPeriodTo] = useState<Date | undefined>(shouldLoadInitially ? initialTo : undefined);
    const [selectedMonth, setSelectedMonth] = useState<string>(
        initialMonth?.value ?? (hasInitialPeriod ? CUSTOM_PERIOD_KEY : defaultMonthOption.value)
    );
    const [dpPeriodFrom, setDpPeriodFrom] = useState<Date | undefined>(initialFrom);
    const [dpPeriodTo, setDpPeriodTo] = useState<Date | undefined>(initialTo);

    const [rows, setRows] = useState<RoadBookRow[] | undefined>(undefined);
    const [selectedDriver, setSelectedDriver] = useState<string[]>(['ALL']);
    const [selectedTruck, setSelectedTruck] = useState<string[]>([ALL_TRUCKS_KEY]);
    const [errorLoading, setErrorLoading] = useState<unknown | undefined>(undefined);
    const [invoicesByDate, setInvoicesByDate] = useState<Record<string, RRFuelInvoiceWithExtras[]>>({});
    const [employeesMap, setEmployeesMap] = useState<Record<string, string>>({});
    const [trucksById, setTrucksById] = useState<Record<string, RRTruckType>>({});
    const visibleRows = useMemo<RoadBookRow[]>(() => {
        if (!rows || rows.length === 0) return [];
        return rows.filter(r => {
            const name = (r.driverName || '').trim();
            const driverMatch = selectedDriver.includes('ALL') || selectedDriver.includes(name);
            const truckKey = r.truckKey || NO_TRUCK_KEY;
            const truckMatch = selectedTruck.includes(ALL_TRUCKS_KEY) || selectedTruck.includes(truckKey);
            return driverMatch && truckMatch;
        });
    }, [rows, selectedDriver, selectedTruck]);

    const drivers = useMemo(() => {
        if (!rows) return [] as string[];
        const names = rows.map(r => (r.driverName || '').trim()).filter(n => n && n.length > 0);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const trucks = useMemo((): Array<{key: string; label: string}> => {
        const map = new Map<string, string>();
        if (rows) {
            rows.forEach(r => {
                const key = r.truckKey || NO_TRUCK_KEY;
                map.set(key, formatTruckLabel(r.truckLabel, r.truckReg));
            });
        }
        Object.keys(trucksById).forEach(id => {
            const truck = trucksById[id];
            map.set(`id:${id}`, formatTruckLabel(truck?.name ?? null, truck?.plate ?? truck?.plate ?? null));
        });
        Object.values(invoicesByDate).forEach(list => {
            list?.forEach(inv => {
                const truckId = getInvoiceTruckId(inv);
                if (truckId) {
                    const truckDoc = trucksById[truckId];
                    const label = formatTruckLabel(truckDoc?.name ?? null, truckDoc?.plate ?? truckDoc?.plate ?? null);
                    if (!map.has(`id:${truckId}`)) map.set(`id:${truckId}`, label);
                }
            });
        });
        return Array.from(map.entries())
            .sort((a, b) => a[1].localeCompare(b[1], 'bg-BG'))
            .map(([key, label]) => ({ key, label }));
    }, [rows, invoicesByDate, trucksById]);

    function setRefPeriod() {
        setPeriodFrom(dateWithZeroTime(dpPeriodFrom));
        setPeriodTo(dateWithMaxTime(dpPeriodTo));
    }

    // Изборът на месец попълва началната и крайната дата с целия съответен период.
    function applyMonthSelection(optionValue: string) {
        const selectedOption = monthOptions.find(option => option.value === optionValue);
        if (!selectedOption) {
            return;
        }

        setSelectedMonth(selectedOption.value);
        setDpPeriodFrom(selectedOption.periodFrom);
        setDpPeriodTo(selectedOption.periodTo);
    }

    useEffect(() => {
        if (!periodFrom || !periodTo) return;
        let cancelled = false;
        const load = async () => {
            setWorking(true);
            setRows(undefined);
            setErrorLoading(undefined);
            try {
                // Вграденият изглед превключва между планираната и отчетената дата.
                const coll = collection(appFirestore, 'logistics').withConverter(logisticsFBConverter);
                const logisticsDateField = showCompletedCourses ? 'reportStartDate' : 'startDate';
                const q = query(
                    coll,
                    where(logisticsDateField, '>=', periodFrom),
                    where(logisticsDateField, '<=', periodTo),
                    orderBy(logisticsDateField, 'asc')
                );
                const snap = await getDocs(q);
                if (cancelled) return;
                const logisticsData = snap.docs
                    .map(d => d.data() as RRLogisticType)
                    .filter(logistic => showCompletedCourses || (
                        logistic.dayType === 'WORKDAY' &&
                        !logistic.workInStore &&
                        logistic.isOwn &&
                        logistic.reportStartDate === null &&
                        logistic.reportEndDate === null
                    ));

                // collect truck ids
                const truckIds = Array.from(new Set(logisticsData.map((l: RRLogisticType) => (l.truck?.id ?? (''))).filter((x: string) => !!x)));

                // fetch truck documents (to get authoritative name/plate) in batches
                const trucksColl = collection(appFirestore, 'trucks').withConverter(trucksFBConverter);
                const trucksMap = new Map<string, RRTruckType>();
                if (truckIds.length > 0) {
                    const batchSize = 10;
                    for (let i = 0; i < truckIds.length; i += batchSize) {
                        const chunk = truckIds.slice(i, i + batchSize);
                        if (chunk.length === 0) continue;
                        try {
                            const qTr = query(trucksColl, where('__name__', 'in', chunk));
                            const snapTr = await getDocs(qTr);
                            snapTr.docs.forEach(d => {
                                const t = d.data() as RRTruckType;
                                trucksMap.set(d.id, t);
                            });
                        } catch (_err) {
                            // ignore truck fetch errors per batch
                        }
                    }
                }
                if (!cancelled && trucksMap.size > 0) {
                    setTrucksById(prev => {
                        const updated = {...prev};
                        trucksMap.forEach((val, id) => { updated[id] = val; });
                        return updated;
                    });
                }

                const createRoadBookRow = (
                    logistic: RRLogisticType,
                    invoiceMap: Map<string, RRFuelInvoiceWithExtras[]>
                ): RoadBookRow => {
                    const startKm = logistic.startKm == null ? null : Number(logistic.startKm);
                    const endKm = logistic.endKm == null ? null : Number(logistic.endKm);
                    const totalKm = (startKm != null && endKm != null) ? (endKm - startKm) : null;
                    const {pricePerKm, priceSource, invoicesSlice} = computePricePerKmFromLogistic(logistic, invoiceMap);
                    const cost = (totalKm != null && pricePerKm != null) ? (totalKm * pricePerKm) : null;
                    const costExclVat = cost != null ? (cost / 1.2) : null;
                    const explicitTruckId = logistic.truck.id ?? '';
                    const truckDoc = explicitTruckId ? trucksMap.get(explicitTruckId) : undefined;
                    const truckLabel = truckDoc ? truckDoc.name : logistic.truck.name;
                    const truckReg = truckDoc ? truckDoc.plate : logistic.truck.plate;
                    const routeText = logistic.startPoint && logistic.endPoint
                        ? `${logistic.startPoint} - ${logistic.endPoint}`
                        : '';
                    const route = logistic.eventNumber && routeText
                        ? `Събитие ${logistic.eventNumber}: ${routeText}`
                        : (logistic.eventNumber ? `Събитие ${logistic.eventNumber}` : routeText);

                    return {
                        id: logistic.id,
                        date: showCompletedCourses
                            ? (logistic.reportStartDate ?? logistic.startDate)
                            : logistic.startDate,
                        plannedStartTime: logistic.startDate,
                        plannedEndTime: logistic.endDate,
                        actualStartTime: logistic.reportStartDate,
                        actualEndTime: logistic.reportEndDate,
                        driverName: logistic.driverName,
                        startKm,
                        route,
                        notes: showCompletedCourses
                            ? (logistic.reportNotes || logistic.planNotes)
                            : logistic.planNotes,
                        endKm,
                        totalKm,
                        cost,
                        costExclVat,
                        pricePerKm,
                        priceSource,
                        truckLabel,
                        invoicesSlice,
                        truckReg,
                        truckId: explicitTruckId,
                        truckKey: buildTruckKey(explicitTruckId, truckLabel, truckReg),
                        pricePerKmExclVat: pricePerKm != null ? pricePerKm / 1.2 : null
                    };
                };

                if (!showCompletedCourses) {
                    if (!cancelled) {
                        setInvoicesByDate({});
                        setEmployeesMap({});
                        setRows(logisticsData.map(logistic => createRoadBookRow(
                            logistic,
                            new Map<string, RRFuelInvoiceWithExtras[]>()
                        )));
                    }
                    return;
                }

                // fetch invoices per truck (last up to 3) in batches
                const invColl = collection(appFirestore, 'fuelInvoices').withConverter(fuelInvoiceConverter);
                const invoicesMap = new Map<string, RRFuelInvoiceWithExtras[]>();
                const batchSize = 10;
                const invoiceFetchErrors: string[] = [];
                type InvBatchResult = { tid: string; snap?: QuerySnapshot<RRFuelInvoiceWithExtras>; err?: unknown };
                const invoiceWindowFrom = periodFrom ? new Date(periodFrom) : null;
                if (invoiceWindowFrom) {
                    invoiceWindowFrom.setDate(invoiceWindowFrom.getDate() - 30);
                }
                for (let i = 0; i < truckIds.length; i += batchSize) {
                    const batch = truckIds.slice(i, i + batchSize);
                    const promises: Promise<InvBatchResult>[] = batch.map(async (tid) => {
                        try {
                            const qInv = invoiceWindowFrom && periodTo
                                ? query(
                                    invColl,
                                    where('truckId', '==', tid),
                                    where('invDate', '>=', invoiceWindowFrom),
                                    where('invDate', '<=', periodTo),
                                    orderBy('invDate', 'asc')
                                )
                                : query(invColl, where('truckId', '==', tid), orderBy('invDate', 'asc'));
                            let s = await getDocs(qInv) as QuerySnapshot<RRFuelInvoiceWithExtras>;
                            if (s.empty && invoiceWindowFrom && periodTo) {
                                const qFallback = query(invColl, where('truckId', '==', tid), orderBy('invDate', 'asc'));
                                s = await getDocs(qFallback) as QuerySnapshot<RRFuelInvoiceWithExtras>;
                            }
                            return { tid, snap: s };
                        } catch (err) {
                            return { tid, err };
                        }
                    });
                    const results = await Promise.all(promises);
                    for (const res of results) {
                        if (res.err) { invoiceFetchErrors.push(`Error fetching invoices for truck ${res.tid}: ${formatErrorMessage(res.err)}`); continue; }
                        if (res.snap && res.snap.docs.length > 0) {
                            const arr = sortFuelInvoicesByDateAndMileage(
                                res.snap.docs.map((documentSnapshot: QueryDocumentSnapshot<RRFuelInvoiceWithExtras>) => documentSnapshot.data())
                            );
                            invoicesMap.set(res.tid, arr);
                        }
                    }
                }
                if (invoiceFetchErrors.length > 0) setErrorLoading(invoiceFetchErrors.join('; '));

                // flatten invoices and group by date string
                const allInvoicesArr: RRFuelInvoiceWithExtras[] = [];
                invoicesMap.forEach((arr) => { arr.forEach((inv) => { if (inv) allInvoicesArr.push(inv); }); });

                const invById: Record<string, RRFuelInvoiceWithExtras> = {};
                for (const inv of allInvoicesArr) {
                    if (!inv || !inv.id) continue;
                    if (inv.invDate && (periodFrom && periodTo) && (inv.invDate < periodFrom || inv.invDate > periodTo)) continue;
                    invById[inv.id] = inv;
                }
                const grouped: Record<string, RRFuelInvoiceWithExtras[]> = {};
                Object.values(invById).forEach(inv => {
                    const d = inv.invDate ? dateToString(inv.invDate) : '';
                    if (!d) return;
                    grouped[d] = grouped[d] || [];
                    grouped[d].push(inv);
                });
                if (!cancelled) setInvoicesByDate(grouped);

                // load employee names for driverIds referenced in grouped invoices
                const driverIds = Array.from(new Set(Object.values(grouped).flatMap(arr => arr.map(inv => inv.driverId).filter((x: string) => !!x))));
                const empMap: Record<string, string> = {};
                const toStr = (v: unknown) => (typeof v === 'string' ? v : (v === undefined || v === null ? '' : String(v)));
                for (let i = 0; i < driverIds.length; i += batchSize) {
                    const chunk = driverIds.slice(i, i + batchSize);
                    if (chunk.length === 0) continue;
                    try {
                        const qEmp = query(collection(appFirestore, 'employees'), where('__name__', 'in', chunk));
                        const snapEmp = await getDocs(qEmp);
                        snapEmp.docs.forEach(d => {
                            const data = d.data() as Record<string, unknown>;
                            empMap[d.id] = toStr(data['name']) || toStr(data['fullName']) || toStr(data['label']) || '';
                        });
                    } catch (_e) { }
                }
                if (!cancelled) setEmployeesMap(empMap);

                // build rows from logistics (only with valid kms)
                const rowsData: RoadBookRow[] = logisticsData
                    .filter((l: RRLogisticType) => {
                        const s = l.startKm == null ? null : Number(l.startKm);
                        const e = l.endKm == null ? null : Number(l.endKm);
                        if (s == null || e == null) return false;
                        if (isNaN(s) || isNaN(e)) return false;
                        return e > s;
                    })
                    .map((logistic: RRLogisticType) => createRoadBookRow(logistic, invoicesMap));

                if (!cancelled) setRows(rowsData);

            } catch (err) {
                if (!cancelled) setErrorLoading(String(err));
            } finally {
                if (!cancelled) setWorking(false);
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [periodFrom, periodTo, showCompletedCourses]);

    // If drivers list changes (e.g. after data load) and selected driver is gone, reset to ALL
    useEffect(() => {
        // Remove any selected drivers that no longer exist in the drivers list (except 'ALL')
        setSelectedDriver(prev => {
            const kept = prev.filter(s => s === 'ALL' || drivers.includes(s));
            if (kept.length === 0) return ['ALL'];
            return kept;
        });
    }, [drivers]);

    useEffect(() => {
        // Remove any selected trucks that are no longer present
        setSelectedTruck(prev => {
            const availableKeys = new Set(trucks.map(t => t.key));
            const kept = prev.filter(s => s === ALL_TRUCKS_KEY || availableKeys.has(s));
            if (kept.length === 0) return [ALL_TRUCKS_KEY];
            return kept;
        });
    }, [trucks]);

    const [priceModal, setPriceModal] = useState<PriceModalState>({show: false, price: null});
    const openPriceModal = (row: RoadBookRow) => {
        if (row.pricePerKm == null) return;
        const note = row.priceSource === 'logistic'
            ? 'Използвана е въведената в логистиката цена за км.'
            : row.priceSource === 'invoice'
                ? 'Изчислена средна цена на км от последните фактури.'
                : undefined;
        setPriceModal({
            show: true,
            price: row.pricePerKm,
            truckLabel: row.truckLabel,
            invoicesSlice: row.invoicesSlice || undefined,
            note
        });
    };
    const closePriceModal = () => setPriceModal(prev => ({...prev, show: false}));
    const exportExcel = (): void => {
        if (visibleRows.length === 0) return;

        const buildVehicleLine = (row: RoadBookRow): string => {
            if (row.truckLabel && row.truckLabel.length > 0) {
                if (row.truckReg && row.truckReg.length > 0) {
                    return `${row.truckLabel} — ${row.truckReg}`;
                }
                return row.truckLabel;
            }
            if (row.truckReg && row.truckReg.length > 0) {
                return row.truckReg;
            }
            return '';
        };

        const worksheetData: Array<Array<string | number>> = [
            ['Дата', 'Шофьор', 'Автомобил', 'Начални км', 'Маршрут', 'Крайни км', 'Общо км', 'Цена/км', 'Цена/км без ДДС']
        ];

        visibleRows.forEach(row => {
            const vehicleLine = buildVehicleLine(row);
            const routeValue = row.route || '';

            worksheetData.push([
                row.date ? dateToString(row.date) : '',
                row.driverName,
                vehicleLine,
                row.startKm != null ? numberWithCommas(row.startKm) : '',
                routeValue,
                row.endKm != null ? numberWithCommas(row.endKm) : '',
                row.totalKm != null ? numberWithCommas(row.totalKm) : '',
                row.pricePerKm != null ? numberWithCommas(row.pricePerKm.toFixed(4)) : '',
                row.pricePerKmExclVat != null ? numberWithCommas(row.pricePerKmExclVat.toFixed(4)) : ''
            ]);
        });

        const workbook = utils.book_new();
        const worksheet = utils.aoa_to_sheet(worksheetData);
        const columnMaxLengths: number[] = new Array(worksheetData[0].length).fill(0);
        worksheetData.forEach(row => {
            row.forEach((cell, idx) => {
                const text = cell == null ? '' : String(cell);
                const length = Math.max(...text.split('\n').map(part => part.length));
                columnMaxLengths[idx] = Math.max(columnMaxLengths[idx] ?? 0, Math.max(length, 1));
            });
        });
        worksheet['!cols'] = columnMaxLengths.map(len => ({wch: Math.min(Math.max(len + 2, 10), 60)}));
        const routeColIndex = 4;
        for (let rowIndex = 1; rowIndex < worksheetData.length; rowIndex++) {
            const cellAddress = utils.encode_cell({c: routeColIndex, r: rowIndex});
            const cell = worksheet[cellAddress];
            if (cell && typeof cell.v === 'string') {
                cell.v = cell.v.replace(/\r\n/g, '\n');
                cell.t = 's';
                const existingAlignment = cell.s?.alignment;
                cell.s = {
                    ...(cell.s || {}),
                    alignment: {
                        ...(existingAlignment || {}),
                        wrapText: true
                    }
                };
            }
        }
        utils.book_append_sheet(workbook, worksheet, 'Пътни книжки');

        const fromLabel = dateToString(periodFrom ?? dpPeriodFrom);
        const toLabel = dateToString(periodTo ?? dpPeriodTo);
        const periodSuffix = (fromLabel && toLabel) ? `_${fromLabel}_${toLabel}` : '';
        const fileName = `RoadBooks${periodSuffix}.xlsx`;

        writeFile(workbook, fileName);
    };

    const tableColumnCount = embedded ? (showCompletedCourses ? 6 : 5) : 9;

    // Helper to render table body - typed to React.ReactNode to satisfy TypeScript
    function renderTableBody(): React.ReactNode {
        // helper: try to extract numeric total from invoice object from many possible fields and string formats
        function extractInvoiceTotal(inv: RRFuelInvoiceWithExtras): number | null {
            if (!inv) return null;
            // parse value helper
            const parse = (v: unknown): number | null => {
                if (v === null || v === undefined) return null;
                if (typeof v === 'number') return !isNaN(v) ? v : null;
                if (typeof v === 'string') {
                    let s = v.replace(/[^0-9.,-]+/g, '').trim();
                    s = s.replace(/\s+/g, '');
                    if (s.indexOf(',') !== -1 && s.indexOf('.') === -1) s = s.replace(/,/g, '.');
                    const n = parseFloat(s);
                    return !isNaN(n) ? n : null;
                }
                return null;
            };

            // Prefer explicit calculation fuelAmount * price (as requested)
            const parsedFuelAmount = parse(inv.fuelAmount);
            const parsedPrice = parse(inv.price);
            if (parsedFuelAmount != null && parsedPrice != null) {
                const calc = parsedFuelAmount * parsedPrice;
                if (!isNaN(calc)) return calc;
            }

            const candidates = [
                'totalForInvoice','total_for_invoice','totalForInvoiceValue','totalAmount','total','amount','grandTotal','sum','totalBrutto','total_net','invoiceTotal','invTotal'
            ];

            // direct candidate fields
            for (const k of candidates) {
                const val = (inv as Record<string, unknown>)[k];
                const p = parse(val);
                if (p != null) {
                    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') console.debug('extractInvoiceTotal: direct', { id: inv?.id ?? inv?.invNumber ?? null, key: k, raw: val, parsed: p });
                    return p;
                }
            }

            // recursive search (depth-limited) for candidate fields in nested objects
            const recursiveFind = (obj: unknown, depth = 0): number | null => {
                if (!obj || depth > 4) return null;
                try {
                    const recObj = obj as Record<string, unknown>;
                    for (const k of candidates) {
                        if (recObj[k] !== undefined) {
                            const pv = parse(recObj[k]);
                            if (pv != null) return pv;
                        }
                    }
                    for (const key of Object.keys(recObj)) {
                        const child = recObj[key];
                        if (child && typeof child === 'object') {
                            const found = recursiveFind(child, depth + 1);
                            if (found != null) return found;
                        } else {
                            const pv = parse(child);
                            if (pv != null) return pv;
                        }
                    }
                } catch (_e) { }
                return null;
            };

            const rec = recursiveFind(inv, 0);
            if (rec != null) {
                if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') console.debug('extractInvoiceTotal: recursive', { id: inv?.id ?? inv?.invNumber ?? null, parsed: rec });
                return rec;
            }

            // try totals object
            try {
                if (inv.totals && typeof inv.totals === 'object') {
                    for (const key of Object.keys(inv.totals)) {
                        const v = parse(inv.totals[key]);
                        if (v != null) {
                            if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') console.debug('extractInvoiceTotal: totals', { id: inv?.id ?? inv?.invNumber ?? null, key, parsed: v });
                            return v;
                        }
                    }
                }
            } catch (_e) { }

            // try arrays of line items: prefer explicit item total, else price*qty
            try {
                const listKeys = ['items','lines','rows','positions','entries','invoiceLines'];
                for (const lk of listKeys) {
                    const arr = (inv as Record<string, unknown>)[lk];
                    if (Array.isArray(arr) && arr.length > 0) {
                        let sum = 0;
                        let found = false;
                        for (const it of arr) {
                            const cand = (it && (it.total ?? it.amount ?? it.lineTotal ?? it.sum ?? it.value));
                            const pv = parse(cand);
                            if (pv != null) { sum += pv; found = true; continue; }
                            // try price*qty
                            const price = parse(it.price ?? it.unitPrice ?? it.pricePerUnit ?? it.netPrice ?? it.grossPrice);
                            const qty = parse(it.qty ?? it.quantity ?? it.units ?? it.count);
                            if (price != null && qty != null) { sum += price * qty; found = true; }
                        }
                        if (found) {
                            if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') console.debug('extractInvoiceTotal: itemsSum', { id: inv?.id ?? inv?.invNumber ?? null, key: lk, sum });
                            return sum;
                        }
                    }
                }
            } catch (_e) { }

            // fallback: recursively scan all fields for first numeric-looking value
            try {
                const keys = Object.keys(inv || {});
                for (const key of keys) {
                    const val = (inv as Record<string, unknown>)[key];
                    if (val != null && typeof val === 'object') {
                        const found = recursiveFind(val, 1);
                        if (found != null) {
                            if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') console.debug('extractInvoiceTotal: fallbackRecursive', { id: inv?.id ?? inv?.invNumber ?? null, key, parsed: found });
                            return found;
                        }
                    } else {
                        const v = parse(val);
                        if (v != null) {
                            if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') console.debug('extractInvoiceTotal: fallbackField', { id: inv?.id ?? inv?.invNumber ?? null, key, parsed: v });
                            return v;
                        }
                    }
                }
            } catch (_e) { }

            if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') console.debug('extractInvoiceTotal: not found', { id: inv?.id ?? inv?.invNumber ?? null, keys: Object.keys(inv).slice(0,20) });
            return null;
        }

        if (visibleRows.length === 0) {
            return (<tr><td colSpan={tableColumnCount} className={'text-center text-muted'}>Няма данни за избрания филтър.</td></tr>);
        }
        const dateGroups: Record<string, RoadBookRow[]> = {};
        const order: string[] = [];
        visibleRows.forEach((r) => {
            const dstr = r.date ? dateToString(r.date) : '';
            if (!dateGroups[dstr]) { dateGroups[dstr] = []; order.push(dstr); }
            dateGroups[dstr].push(r);
        });

        return order.map(dstr => (
             <React.Fragment key={dstr}>
                {dateGroups[dstr].map((r: RoadBookRow, idx2: number) => (
                     <tr key={(r.id || r.date) + '_' + idx2}>
                        <th scope="row">{r.date ? dateToString(r.date) : ''}</th>
                        {embedded && <td>{formatTimeRange(r.plannedStartTime, r.plannedEndTime)}</td>}
                        {embedded && showCompletedCourses && <td>{formatTimeRange(r.actualStartTime, r.actualEndTime)}</td>}
                        <td>{r.driverName}</td>
                        <td>{formatTruckLabel(r.truckLabel, r.truckReg)}</td>
                        {!embedded && <td className={'text-end'}>{r.startKm != null ? numberWithCommas(r.startKm) : ''}</td>}
                        <td className={'text-start'}>
                            <div>{r.route}</div>
                            {r.notes ? <div className={'small text-muted mt-1'}>{r.notes}</div> : null}
                        </td>
                        {!embedded && <td className={'text-end'}>{r.endKm != null ? numberWithCommas(r.endKm) : ''}</td>}
                        {!embedded && <td className={'text-end'}>{r.totalKm != null ? numberWithCommas(r.totalKm) : ''}</td>}
                        {!embedded && (() => {
                            const priceValue = r.pricePerKm != null ? `${numberWithCommas(r.pricePerKm.toFixed(4))}` : '';
                            const clickable = r.pricePerKm != null;
                            return (
                                <td
                                    className={'text-end'}
                                    style={{cursor: clickable ? 'pointer' : 'default', color: clickable ? '#0d6efd' : 'inherit', textDecoration: clickable ? 'underline' : 'none'}}
                                    onClick={() => clickable && openPriceModal(r)}
                                >
                                    {priceValue}
                                </td>
                            );
                        })()}
                        {!embedded && <td className={'text-end'}>{r.pricePerKmExclVat != null ? numberWithCommas(r.pricePerKmExclVat.toFixed(4)) : ''}</td>}
                    </tr>
                ))}

                {((): React.ReactNode => {
                     const invs: RRFuelInvoiceWithExtras[] = invoicesByDate[dstr] || [];
                     const filteredInvs = invs.filter(inv => {
                        const driverId = inv.driverId || '';
                        const name = driverId && employeesMap[driverId] ? employeesMap[driverId] : (inv.driverName || inv.driverLabel || '');
                        const driverMatch = selectedDriver.includes('ALL') || selectedDriver.includes(name);
                        if (!driverMatch) return false;
                        if (selectedTruck.includes(ALL_TRUCKS_KEY)) return true;
                        const truckId = getInvoiceTruckId(inv);
                        if (truckId) return selectedTruck.includes(`id:${truckId}`);
                        return selectedTruck.includes(NO_TRUCK_KEY);
                     });
                     if (!filteredInvs || filteredInvs.length === 0) return null;
                     return filteredInvs.map((inv: RRFuelInvoiceWithExtras, invIdx: number) => {
                         const invDate = inv.invDate ? dateToString(inv.invDate) : '';
                         const driverId = inv.driverId || '';
                         const driverName = driverId && employeesMap[driverId] ? employeesMap[driverId] : (inv.driverName || inv.driverLabel || '');
                         const startKm = inv.startKm ?? inv.start_km ?? null;
                         const endKm = inv.endKm ?? inv.end_km ?? null;
                         const totalKmInv = inv.totalKm ?? inv.total_km ?? null;
                         const invNumber = inv.invNumber ?? inv.invoiceNumber ?? inv.number ?? inv.inv_no ?? '';
                         // extract total using robust helper
                         const totalForInvoice = extractInvoiceTotal(inv);
                         const totalExclVat = totalForInvoice != null ? (totalForInvoice * 0.8) : null;

                         return (
                            <tr key={invIdx + '_inv_' + dstr} className={'fw-bold text-primary'}>
                                <td>{invDate || '-'}</td>
                                {embedded && <td/>}
                                {embedded && showCompletedCourses && <td/>}
                                <td>{driverName || '-'}</td>
                                <td>-</td>
                                {!embedded && <td className={'text-end'}>{startKm != null ? numberWithCommas(Number(startKm)) : '-'}</td>}
                                <td className={'text-start'}>{invNumber ? `Фактура № ${invNumber}` : 'Фактура'}</td>
                                {!embedded && <td className={'text-end'}>{endKm != null ? numberWithCommas(Number(endKm)) : '-'}</td>}
                                {!embedded && <td className={'text-end'}>{totalKmInv != null ? numberWithCommas(Number(totalKmInv)) : '-'}</td>}
                                {!embedded &&
                                    // prepare small debug info for title and display
                                    (() => {
                                        const rawCandidates = { totalForInvoice: inv.totalForInvoice, total: inv.total, totalAmount: inv.totalAmount, amount: inv.amount };
                                        const keys = Object.keys(inv || {}).slice(0, 10);
                                        const title = totalForInvoice != null ? `Източник: ${totalForInvoice.toFixed(2)}` : `rawCandidates: ${JSON.stringify(rawCandidates)}; keys=${JSON.stringify(keys)}`;

                                        let displayStr: string = '-';
                                        if (totalForInvoice != null) {
                                            displayStr = numberWithCommas(Number(totalForInvoice).toFixed(2)) + ' €';
                                        } else {
                                            const candVals = [inv.totalForInvoice, inv.total, inv.totalAmount, inv.amount];
                                            const firstRaw = candVals.find(v => v != null);
                                            if (firstRaw != null) {
                                                // try to parse number from raw
                                                try {
                                                    if (typeof firstRaw === 'number' && !isNaN(firstRaw)) displayStr = numberWithCommas(Number(firstRaw).toFixed(2)) + ' €';
                                                    else if (typeof firstRaw === 'string') {
                                                        let s = firstRaw.replace(/[^0-9.,-]+/g, '').trim(); s = s.replace(/\s+/g, ''); if (s.indexOf(',') !== -1 && s.indexOf('.') === -1) s = s.replace(/,/g, '.'); const n = parseFloat(s); if (!isNaN(n)) displayStr = numberWithCommas(n.toFixed(2)) + ' €'; else displayStr = String(firstRaw).slice(0, 30);
                                                    } else displayStr = String(firstRaw).slice(0, 30);
                                                } catch (_e) { displayStr = String(firstRaw).slice(0, 30); }
                                            }
                                        }

                                        return <td className={'text-end'} title={title}>{displayStr}</td>;
                                    })()
                                }
                                {!embedded && <td className={'text-end'}>{totalExclVat != null ? numberWithCommas(Number(totalExclVat).toFixed(2)) + ' €' : '-'}</td>}
                            </tr>
                        );
                    });
                })()}
            </React.Fragment>
         ));
     }

    return (
        <Container fluid>
            {
                !embedded && <>
                    <h1>Справки и отчети</h1>
                    <h3>Пътни книжки</h3>
                </>
            }

            <Row className={'g-2 mt-3 flex-nowrap'}>
                <Col>
                    <Row className={'lh-2-5em flex-nowrap'}>
                        <Col xs={'auto'}>
                            <Row>
                                <Col xs={'auto'} className={'lh-2-5em'}>Месец:</Col>
                                <Col xs={'auto'}>
                                    <FormSelect
                                        className={'mb-2'}
                                        disabled={working}
                                        value={selectedMonth}
                                        onChange={event => applyMonthSelection(event.target.value)}
                                    >
                                        {
                                            selectedMonth === CUSTOM_PERIOD_KEY &&
                                            <option value={CUSTOM_PERIOD_KEY}>Избран период</option>
                                        }
                                        {
                                            monthOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))
                                        }
                                    </FormSelect>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={'auto'}>
                            <Row>
                                <Col xs={'auto'} className={'lh-2-5ем'}>Начална дата:</Col>
                                <Col xs={'auto'}>
                                    <InputGroup className="mb-2 w-180px flex-nowrap">
                                        <ReactDatePicker disabled={working} id={'rb_periodFromDate'} className={'form-control flex-nowrap'} selected={dpPeriodFrom} onChange={date => {
                                            setDpPeriodFrom(date || defaultMonthOption.periodFrom);
                                            setSelectedMonth(CUSTOM_PERIOD_KEY);
                                        }} value={dateToString(dpPeriodFrom)} maxDate={dpPeriodTo} />
                                        <label htmlFor={'rb_periodFromDate'}>
                                            <InputGroup.Text className={'lh-2-5ем'}><FontAwesomeIcon icon={faCalendar} size={'xl'} /></InputGroup.Text>
                                        </label>
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Col>

                        <Col xs={'auto'}>
                            <Row>
                                <Col xs={'auto'} className={'lh-2-5ем'}>Крайна дата:</Col>
                                <Col xs={'auto'}>
                                    <InputGroup className="mb-2 w-180px flex-nowrap">
                                        <ReactDatePicker disabled={working} id={'rb_periodToDate'} className={'form-control flex-nowrap'} selected={dpPeriodTo} onChange={date => {
                                            setDpPeriodTo(date || today);
                                            setSelectedMonth(CUSTOM_PERIOD_KEY);
                                        }} value={dateToString(dpPeriodTo)} minDate={dpPeriodFrom} />
                                        <label htmlFor={'rb_periodToDate'}>
                                            <InputGroup.Text className={'lh-2-5ем'}><FontAwesomeIcon icon={faCalendar} size={'xl'} /></InputGroup.Text>
                                        </label>
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Col>

                        <Col>
                            {!working && <>
                                <Button onClick={setRefPeriod}>Изчисли</Button>&nbsp;
                                {
                                    embedded &&
                                    <Button
                                        variant={'outline-primary'}
                                        onClick={() => setShowCompletedCourses(current => !current)}
                                    >
                                        {
                                            showCompletedCourses
                                                ? 'Покажи неизпълнените курсове'
                                                : 'Покажи изпълнените курсове'
                                        }
                                    </Button>
                                }&nbsp;
                                {!embedded && <Button onClick={exportExcel} variant={'outline-success'}><FontAwesomeIcon icon={faFileExcel}/></Button>}&nbsp;
                            </>}
                        </Col>
                    </Row>
                </Col>
            </Row>


            <Row className={'g-2 mt-2'}>
                <Col>
                    <div className={'d-flex flex-row flex-wrap gap-2'}>
                        <Button
                            size={'sm'}
                            variant={selectedDriver.includes('ALL') ? 'primary' : 'outline-primary'}
                            onClick={() => setSelectedDriver(['ALL'])}
                        >
                            Всички
                        </Button>
                        {drivers.map(d => (
                            <Button
                                key={d}
                                size={'sm'}
                                variant={selectedDriver.includes(d) ? 'primary' : 'outline-primary'}
                                onClick={() => setSelectedDriver(prev => {
                                    // toggle d; remove 'ALL' if present
                                    const has = prev.includes(d);
                                    let next = has ? prev.filter(x => x !== d) : [...prev.filter(x => x !== 'ALL'), d];
                                    if (next.length === 0) next = ['ALL'];
                                    return next;
                                })}
                            >
                                {d}
                            </Button>
                        ))}
                    </div>
                </Col>
            </Row>

            <Row className={'g-2 mt-2'}>
                <Col>
                    <div className={'d-flex flex-row flex-wrap gap-2'}>
                        <Button
                            size={'sm'}
                            variant={selectedTruck.includes(ALL_TRUCKS_KEY) ? 'primary' : 'outline-primary'}
                            onClick={() => setSelectedTruck([ALL_TRUCKS_KEY])}
                        >
                            Всички автомобили
                        </Button>
                        {trucks.map((truckOption) => (
                            <Button
                                key={truckOption.key}
                                size={'sm'}
                                variant={selectedTruck.includes(truckOption.key) ? 'primary' : 'outline-primary'}
                                onClick={() => setSelectedTruck(prev => {
                                    const has = prev.includes(truckOption.key);
                                    let next = has ? prev.filter(x => x !== truckOption.key) : [...prev.filter(x => x !== ALL_TRUCKS_KEY), truckOption.key];
                                    if (next.length === 0) next = [ALL_TRUCKS_KEY];
                                    return next;
                                })}
                            >
                                {truckOption.label}
                            </Button>
                        ))}
                    </div>
                </Col>
            </Row>

            <Row className={'g-2 mt-3'}>
                <Col>
                    <>
                        {errorLoading && <Alert variant={'danger'}>Грешка при зареждане: {formatErrorMessage(errorLoading)}</Alert>}
                        <RRTable>
                            <thead>
                            <tr>
                                <th>ДАТА</th>
                                {embedded && <th>ПЛАНИРАНО ОТ–ДО</th>}
                                {embedded && showCompletedCourses && <th>ИЗПЪЛНЕНО ОТ–ДО</th>}
                                <th>ШОФЬОР</th>
                                <th>МПС</th>
                                {!embedded && <th className={'text-end'}>НАЧАЛНИ КМ</th>}
                                <th>МАРШРУТ</th>
                                {!embedded && <th className={'text-end'}>КРАЙНИ КМ</th>}
                                {!embedded && <th className={'text-end'}>ОБЩО КМ</th>}
                                {!embedded && <th className={'text-end'}>Ст-ст</th>}
                                {!embedded && <th className={'text-end'}>Ст-ст без ДДС</th>}
                            </tr>
                            </thead>
                            <tbody>
                            {rows && rows.length > 0 ? renderTableBody() : (
                                <tr><td colSpan={tableColumnCount} className={'text-center text-muted'}>Няма данни за избрания период.</td></tr>
                            )}
                            </tbody>
                        </RRTable>
                    </>
                 </Col>
             </Row>

            <PriceCalcModal
                show={priceModal.show}
                onHide={closePriceModal}
                price={priceModal.price}
                truckLabel={priceModal.truckLabel}
                invoicesSlice={priceModal.invoicesSlice ?? undefined}
                note={priceModal.note}
            />

        </Container>
    );
}

export default RoadBooksView;
